<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { onMount } from 'svelte';
  import { replace, push } from 'svelte-spa-router';
  import { session } from '../lib/session';
  import { adminMode } from '../lib/admin-mode';
  import { api, ApiError } from '../lib/api';
  import { adminState } from '../lib/admin-state';
  import type { ActivityStatsDTO, ActivityMember } from '../lib/types';

  const MONTH_NAMES = [
    'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
    'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень',
  ];
  const MONTH_NAMES_GEN = [
    'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
    'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
  ];
  // Monday-first weekday labels; maps to backend dow (0=Sun..6=Sat).
  const WD_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
  const WD_ORDER = [1, 2, 3, 4, 5, 6, 0];
  const pad2 = (n: number): string => String(n).padStart(2, '0');

  let month = $state(adminState.activityMonth); // YYYY-MM
  let stats = $state<ActivityStatsDTO | null>(null);
  let loading = $state(false);
  let error = $state<string | null>(null);

  $effect(() => {
    adminState.activityMonth = month;
  });

  function monthLabel(m: string): string {
    const [y, mm] = m.split('-').map(Number);
    return `${MONTH_NAMES[mm - 1]} ${y}`;
  }
  function formatFullDay(iso: string): string {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    return `${d} ${MONTH_NAMES_GEN[m - 1]} ${y}`;
  }

  async function load(): Promise<void> {
    loading = true;
    error = null;
    try {
      stats = await api.adminActivityStats(month);
    } catch (err) {
      error = err instanceof ApiError ? `Помилка (${err.code})` : 'Не вдалось завантажити';
    } finally {
      loading = false;
    }
  }

  function shiftMonth(delta: number): void {
    const [y, m] = month.split('-').map(Number);
    const dt = new Date(y, m - 1 + delta, 1);
    month = `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}`;
    stats = null;
    load();
  }

  // ── Heat-map grid (calendar, Monday-first) ──
  type Cell = { day: number; date: string; count: number } | null;
  const grid = $derived.by((): Cell[] => {
    if (!stats) return [];
    const counts = new Map(stats.heatmap.map((h) => [h.date, h.count]));
    const [y, m] = month.split('-').map(Number);
    const firstDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay(); // 0=Sun
    const lead = (firstDow + 6) % 7; // Monday-first offset
    const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const cells: Cell[] = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= days; d++) {
      const date = `${month}-${pad2(d)}`;
      cells.push({ day: d, date, count: counts.get(date) ?? 0 });
    }
    return cells;
  });
  const maxDay = $derived(stats ? Math.max(1, ...stats.heatmap.map((h) => h.count)) : 1);
  /** 0 → transparent; otherwise a purple tint that scales with intensity. */
  function cellBg(count: number): string {
    if (count <= 0) return 'var(--bg2)';
    const pct = Math.round(18 + (count / maxDay) * 82);
    return `color-mix(in srgb, var(--pu) ${pct}%, transparent)`;
  }

  // ── Weekday averages (reordered Mon-first), scoped to month or all-time ──
  let wdScope = $state<'month' | 'all'>('month');
  let allWeekday = $state<Array<{ dow: number; avg: number; views: number }> | null>(null);
  let wdLoading = $state(false);

  async function setWdScope(scope: 'month' | 'all'): Promise<void> {
    wdScope = scope;
    // All-time weekday doesn't depend on the month, so fetch once and cache.
    if (scope === 'all' && allWeekday === null && !wdLoading) {
      wdLoading = true;
      try {
        allWeekday = (await api.adminActivityWeekday()).weekday;
      } catch {
        allWeekday = [];
      } finally {
        wdLoading = false;
      }
    }
  }

  const weekdayView = $derived.by(() => {
    const src = wdScope === 'all' ? allWeekday : stats?.weekday ?? null;
    if (!src) return [] as Array<{ label: string; avg: number; views: number }>;
    const byDow = new Map(src.map((w) => [w.dow, w]));
    return WD_ORDER.map((dow, i) => {
      const w = byDow.get(dow);
      return { label: WD_LABELS[i], avg: w?.avg ?? 0, views: w?.views ?? 0 };
    });
  });
  const maxWd = $derived(Math.max(1e-6, ...weekdayView.map((w) => w.avg)));

  // Full weekday names for the click-to-inspect info line.
  const WD_FULL = ['Понеділок', 'Вівторок', 'Середа', 'Четвер', "П'ятниця", 'Субота', 'Неділя'];
  const r1 = (n: number): number => Math.round(n * 10) / 10;
  let wdSel = $state<number | null>(null); // index in weekdayView
  let hourSel = $state<number | null>(null); // hour 0..23

  // ── Hourly distribution ──
  const maxHour = $derived(stats ? Math.max(1e-6, ...stats.hourly.map((h) => h.avg)) : 1);

  // ── Day drill-in (people active on one day) ──
  let dayDate = $state<string | null>(null);
  let dayTitle = $state('');
  let dayItems = $state<ActivityMember[]>([]);
  let dayTotal = $state(0);
  let dayPage = $state(1);
  let dayHasMore = $state(false);
  let dayLoading = $state(false);
  let dayError = $state<string | null>(null);

  async function loadDay(page: number): Promise<void> {
    if (!dayDate) return;
    dayLoading = true;
    dayError = null;
    try {
      const res = await api.adminActivityDay(dayDate, page);
      dayItems = page === 1 ? res.items : [...dayItems, ...res.items];
      dayTotal = res.total;
      dayPage = res.page;
      dayHasMore = res.hasMore;
    } catch {
      dayError = 'Не вдалось завантажити список';
    } finally {
      dayLoading = false;
    }
  }

  function openDay(date: string): void {
    dayDate = date;
    dayTitle = `Активність · ${formatFullDay(date)}`;
    dayItems = [];
    dayTotal = 0;
    dayPage = 1;
    dayHasMore = false;
    adminState.activityDay = date;
    loadDay(1);
  }
  function closeDay(): void {
    dayDate = null;
    adminState.activityDay = null;
  }
  function openContact(telegramId: string): void {
    // Keep adminState.activityDay so the drill-in reopens when we come back.
    push(`/admin/user/${telegramId}`);
  }

  onMount(async () => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    adminMode.set('admin');
    await load();
    // Reopen the day drill-in if we're returning from a contact page.
    if (adminState.activityDay) openDay(adminState.activityDay);
  });
</script>

<div class="screen">
  <div class="admin-header">
    <a href="#/admin/dashboard" class="back-link">← Назад</a>
    <div class="header-title">Активність</div>
    <span class="header-spacer"></span>
  </div>

  <div class="scroll">
    <div class="month-nav">
      <button type="button" class="month-arrow" onclick={() => shiftMonth(-1)} aria-label="Попередній місяць">‹</button>
      <div class="month-label">{monthLabel(month)}</div>
      <button type="button" class="month-arrow" onclick={() => shiftMonth(1)} aria-label="Наступний місяць">›</button>
    </div>

    {#if loading && !stats}
      <div class="placeholder">Завантажуємо…</div>
    {:else if error}
      <div class="placeholder err">{error}</div>
    {:else if stats}
      <div class="total-line">Усього активних за місяць: <b>{stats.totalActive}</b></div>

      <!-- Heat-map calendar -->
      <div class="sec-title">Календар активності</div>
      <div class="cal-card">
        <div class="cal-head">
          {#each WD_LABELS as w (w)}<div class="cal-head-cell">{w}</div>{/each}
        </div>
        <div class="cal-grid">
          {#each grid as c, i (i)}
            {#if c === null}
              <div class="cal-cell empty"></div>
            {:else}
              <button
                type="button"
                class="cal-cell"
                class:clickable={c.count > 0}
                style="background: {cellBg(c.count)}"
                title={`${formatFullDay(c.date)}: ${c.count}`}
                onclick={() => c.count > 0 && openDay(c.date)}
              >
                <span class="cal-day">{c.day}</span>
                {#if c.count > 0}<span class="cal-count">{c.count}</span>{/if}
              </button>
            {/if}
          {/each}
        </div>
      </div>

      <!-- Weekday averages -->
      <div class="wd-head">
        <div class="sec-title">Середня активність по днях тижня</div>
        <div class="scope-toggle">
          <button type="button" class="scope-chip" class:on={wdScope === 'month'} onclick={() => setWdScope('month')}>Місяць</button>
          <button type="button" class="scope-chip" class:on={wdScope === 'all'} onclick={() => setWdScope('all')}>Весь час</button>
        </div>
      </div>
      <div class="chart-card">
        {#if wdScope === 'all' && wdLoading && allWeekday === null}
          <div class="wd-loading">Рахуємо…</div>
        {:else}
          <div class="chart-bar-row">
            {#each weekdayView as w, i (w.label)}
              <button
                type="button"
                class="chart-bar"
                class:active-bar={w.avg > 0}
                class:sel={wdSel === i}
                style="height: {Math.max(4, Math.round((w.avg / maxWd) * 100))}%"
                aria-label={w.label}
                onclick={() => (wdSel = wdSel === i ? null : i)}
              ></button>
            {/each}
          </div>
          <div class="chart-labels">
            {#each weekdayView as w, i (w.label)}
              <button type="button" class="chart-label" class:sel={wdSel === i} onclick={() => (wdSel = wdSel === i ? null : i)}>{w.label}</button>
            {/each}
          </div>
          {#if wdSel !== null && weekdayView[wdSel]}
            <div class="bar-info">
              <b>{WD_FULL[wdSel]}</b> · у середньому <b>{r1(weekdayView[wdSel].avg)}</b> активних · <b>{r1(weekdayView[wdSel].views)}</b> переглядів на день
            </div>
          {/if}
        {/if}
      </div>

      <!-- Hourly distribution -->
      <div class="sec-title">Активність по годинах</div>
      <div class="chart-card">
        <div class="chart-bar-row">
          {#each stats.hourly as h (h.hour)}
            <button
              type="button"
              class="chart-bar hour-bar"
              class:active-bar={h.avg > 0}
              class:sel={hourSel === h.hour}
              style="height: {Math.max(3, Math.round((h.avg / maxHour) * 100))}%"
              aria-label={`${pad2(h.hour)}:00`}
              onclick={() => (hourSel = hourSel === h.hour ? null : h.hour)}
            ></button>
          {/each}
        </div>
        <div class="chart-labels">
          {#each stats.hourly as h (h.hour)}
            <div class="chart-label hour-label">{h.hour % 6 === 0 ? h.hour : ''}</div>
          {/each}
        </div>
        {#if hourSel !== null && stats.hourly[hourSel]}
          <div class="bar-info">
            <b>{pad2(hourSel)}:00</b> · у середньому <b>{r1(stats.hourly[hourSel].avg)}</b> активних · <b>{r1(stats.hourly[hourSel].views)}</b> переглядів на день
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>

{#if dayDate}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="modal-backdrop" aria-hidden="true" onclick={closeDay}></div>
  <div class="modal-sheet" role="dialog" aria-modal="true" aria-label={dayTitle}>
    <div class="modal-head">
      <div class="modal-title">{dayTitle}{dayTotal ? ` · ${dayTotal}` : ''}</div>
      <button type="button" class="modal-close" onclick={closeDay} aria-label="Закрити"><Icon name="x" /></button>
    </div>
    <div class="modal-body">
      {#if dayLoading && dayItems.length === 0}
        <div class="members-empty">Завантажуємо…</div>
      {:else if dayError}
        <div class="members-empty err">{dayError}</div>
      {:else if dayItems.length === 0}
        <div class="members-empty">Нікого не було</div>
      {:else}
        <div class="members-list">
          {#each dayItems as a (a.telegramId)}
            <div
              class="member-row"
              role="button"
              tabindex="0"
              onclick={() => openContact(a.telegramId)}
              onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openContact(a.telegramId)}
            >
              <div class="member-av">
                {#if a.photoUrl}<img src={a.photoUrl} alt="" />{:else}{(a.fullName?.charAt(0) || '?').toUpperCase()}{/if}
              </div>
              <div class="member-main">
                <div class="member-name">{a.fullName}</div>
                <div class="member-sub">
                  {#if a.username}@{a.username}{:else if a.email}{a.email}{:else if a.phone}{a.phone}{:else if a.telegramId.startsWith('-')}контакт з оплати{:else}ID {a.telegramId}{/if}
                </div>
              </div>
              <div class="member-detail">{a.materialsCount} {a.materialsCount === 1 ? 'матеріал' : 'матеріалів'}</div>
            </div>
          {/each}
        </div>
        {#if dayHasMore}
          <button type="button" class="members-more" disabled={dayLoading} onclick={() => loadDay(dayPage + 1)}>
            {dayLoading ? 'Завантажую…' : `Завантажити ще (${dayTotal - dayItems.length})`}
          </button>
        {/if}
      {/if}
    </div>
  </div>
{/if}

<style>
  .screen { display: flex; flex-direction: column; flex: 1; min-height: 0; background: var(--bg); }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px; color: var(--text-light); display: flex; align-items: center; gap: 10px; flex-shrink: 0;
  }
  .back-link { font-size: 12px; color: rgba(251, 246, 236, 0.9); text-decoration: none; }
  .header-title { flex: 1; text-align: center; font-size: 14px; font-weight: 600; }
  .header-spacer { width: 40px; }
  .scroll { flex: 1; overflow-y: auto; padding: 16px 16px 100px; }

  .month-nav { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
  .month-arrow {
    width: 34px; height: 34px; border-radius: 8px; border: 1px solid var(--border2);
    background: var(--bg2); color: var(--text); font-size: 18px; line-height: 1; cursor: pointer; font-family: inherit;
  }
  .month-arrow:active { background: var(--pu-light); }
  .month-label { flex: 1; text-align: center; font-size: 14px; font-weight: 700; color: var(--text); }

  .total-line { font-size: 13px; color: var(--text-muted); margin-bottom: 16px; }
  .total-line b { color: var(--text); font-size: 15px; }

  .sec-title {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    color: var(--text-muted); margin: 0 2px 8px;
  }

  .wd-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .scope-toggle { display: flex; gap: 4px; margin-bottom: 8px; flex-shrink: 0; }
  .scope-chip {
    border: 1px solid var(--border2); background: var(--bg); color: var(--text-muted);
    font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 999px;
    cursor: pointer; font-family: inherit;
  }
  .scope-chip.on { border-color: var(--pu); color: var(--accent-text); background: var(--pu-light); }
  .wd-loading { padding: 24px 10px; text-align: center; color: var(--text-muted); font-size: 12px; }

  /* Calendar heat-map */
  .cal-card { border: 0.5px solid var(--border); border-radius: 12px; padding: 12px; margin-bottom: 18px; }
  .cal-head, .cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 5px; }
  .cal-head { margin-bottom: 5px; }
  .cal-head-cell { text-align: center; font-size: 10px; color: var(--text-muted); font-weight: 600; }
  .cal-cell {
    aspect-ratio: 1; border: 0.5px solid var(--border); border-radius: 8px; padding: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    background: var(--bg2); color: var(--text); font-family: inherit; position: relative;
  }
  .cal-cell.empty { border: none; background: transparent; }
  .cal-cell.clickable { cursor: pointer; }
  .cal-cell.clickable:hover { filter: brightness(1.05); }
  .cal-day { font-size: 11px; font-weight: 600; line-height: 1; }
  .cal-count { font-size: 9px; color: var(--text); opacity: 0.75; margin-top: 2px; line-height: 1; }

  /* Bar charts (weekday + hourly) */
  .chart-card { border: 0.5px solid var(--border); border-radius: 12px; padding: 14px; margin-bottom: 18px; }
  .chart-bar-row { display: flex; align-items: flex-end; gap: 5px; height: 70px; margin-bottom: 6px; }
  .chart-bar {
    flex: 1; border-radius: 4px 4px 0 0; background: var(--pu-light);
    border: none; padding: 0; cursor: pointer;
  }
  .chart-bar.active-bar { background: linear-gradient(180deg, var(--pu2), var(--pu)); }
  .chart-bar.sel { outline: 2px solid var(--text); outline-offset: 1px; }
  .chart-bar-row:has(.hour-bar) { gap: 2px; }
  .chart-labels { display: flex; gap: 5px; }
  .chart-label {
    flex: 1; font-size: 9px; text-align: center; color: var(--text-muted);
    background: none; border: none; padding: 0; cursor: pointer; font-family: inherit;
  }
  .chart-label.sel { color: var(--accent-text); font-weight: 700; }
  .chart-labels:has(.hour-label) { gap: 2px; }
  .bar-info {
    margin-top: 10px; padding: 8px 10px; border-radius: 8px; background: var(--bg2);
    font-size: 12px; color: var(--text-muted); line-height: 1.4;
  }
  .bar-info b { color: var(--text); font-weight: 700; }

  .placeholder {
    padding: 30px 16px; text-align: center; color: var(--text-muted); font-size: 13px;
    border: 1px dashed var(--border2); border-radius: 12px; background: var(--bg2);
  }
  .placeholder.err { color: var(--status-inactive); }

  /* Day drill-in modal (bottom sheet) */
  .modal-backdrop { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.55); z-index: 1000; }
  .modal-sheet {
    position: fixed; left: 50%; bottom: 0; transform: translateX(-50%); width: min(100%, 480px);
    max-height: 85vh; background: var(--bg); border-radius: 16px 16px 0 0; z-index: 1001;
    display: flex; flex-direction: column; box-shadow: 0 -16px 40px rgba(0, 0, 0, 0.25);
    animation: sheet-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes sheet-up { from { transform: translate(-50%, 100%); } to { transform: translate(-50%, 0); } }
  .modal-head {
    display: flex; align-items: center; justify-content: space-between; padding: 16px 18px 12px;
    border-bottom: 0.5px solid var(--border); flex-shrink: 0;
  }
  .modal-title { font-size: 16px; font-weight: 700; color: var(--text); }
  .modal-close {
    width: 30px; height: 30px; border-radius: 50%; background: var(--bg2); border: none;
    color: var(--text-muted); cursor: pointer; font-size: 14px; font-family: inherit;
    display: flex; align-items: center; justify-content: center;
  }
  .modal-body { padding: 14px 18px 18px; overflow-y: auto; flex: 1; min-height: 0; }

  .members-list { display: flex; flex-direction: column; gap: 6px; }
  .member-row {
    display: flex; align-items: center; gap: 11px; padding: 9px 10px; border: 1px solid var(--border);
    border-radius: 11px; background: var(--bg); cursor: pointer; text-align: left;
  }
  .member-row:hover { border-color: var(--outline); }
  .member-av {
    width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
    background: linear-gradient(135deg, var(--pu), var(--pu3)); color: var(--text-light);
    display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 600; overflow: hidden;
  }
  .member-av img { width: 100%; height: 100%; object-fit: cover; }
  .member-main { flex: 1; min-width: 0; }
  .member-name { font-size: 13px; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .member-sub { font-size: 11px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 2px; }
  .member-detail { flex-shrink: 0; font-size: 10px; color: var(--text-muted); text-align: right; line-height: 1.3; }
  .members-empty { padding: 28px 10px; text-align: center; color: var(--text-muted); font-size: 13px; }
  .members-empty.err { color: var(--status-inactive); }
  .members-more {
    width: 100%; margin-top: 10px; padding: 11px; border: 1px solid var(--border2); border-radius: 10px;
    background: var(--bg); color: var(--text); font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit;
  }
  .members-more:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
